import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { Chip, FormControl } from "@mui/material";
import { StyledTextFieldBootstrap } from "../../doctor-console/StyledComponents";
// Inventory's own client — it already resolves the inventory base URL and attaches the
// org/auth headers, so the store list here goes through the same path the Inventory
// module uses rather than a second hand-rolled call.
import { getStores } from "../../inventory/api/stores";
import { toInventoryList } from "../../inventory/api/inventory-request";
import { storeDisplayName } from "../../inventory/utils/storeEntity";

/**
 * Store picker shared by the add / edit user screens (User Management, Nurse
 * Management, Doctor Management).
 *
 * Two ways to drive it, because the add forms use react-hook-form and the edit
 * modals keep their row in plain state:
 *   <StoreSelectField control={control} />                     // react-hook-form
 *   <StoreSelectField value={ids} onChange={setIds} />         // controlled
 *
 * The value is a plain array of store id strings, which is what add_new_user/
 * and edit_user/ take as `stores`.
 */

// "All" is a sentinel, not a store id — it is what the payload carries when the user
// picks every store, and the backend normalises it like any other single value.
export const ALL_STORES_VALUE = "All";
const ALL_STORES_OPTION = { store_id: ALL_STORES_VALUE, store_name: "All Stores" };

// add_new_user/ and edit_user/ reject an empty `stores` with "Store is required",
// and treat a missing key as "leave the assignment alone" — so callers send the
// key only when this returns something.
export function toStoresPayload(value) {
  return [...new Set((value || []).map(String).filter(Boolean))];
}

function StoreSelectField({
  control,
  name = "stores",
  label = "Store",
  value,
  onChange,
  className = "col-sm-6 col-md-6 col-lg-6",
}) {
  const [stores, setStores] = useState([]);
  const [storesStatus, setStoresStatus] = useState("idle"); // idle | loading | loaded | error

  // Every user type can be assigned stores, so the list is loaded once on mount.
  useEffect(() => {
    let cancelled = false;
    setStoresStatus("loading");
    getStores()
      .then((data) => {
        if (cancelled) return;
        setStores(toInventoryList(data).filter((s) => s?.is_active !== false));
        setStoresStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load inventory stores:", err);
        setStores([]);
        setStoresStatus("error");
        toast.error("Could not load stores. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const renderAutocomplete = (selectedIds, setSelectedIds) => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={[ALL_STORES_OPTION, ...stores]}
      loading={storesStatus === "loading"}
      getOptionLabel={(option) => storeDisplayName(option)}
      isOptionEqualToValue={(option, val) =>
        String(option.store_id ?? option.id) === String(val?.store_id ?? val?.id)
      }
      noOptionsText={
        storesStatus === "loading"
          ? "Loading stores…"
          : storesStatus === "error"
            ? "Could not load stores"
            : "No stores found"
      }
      renderTags={(val, getTagProps) =>
        val.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            size="small"
            label={storeDisplayName(option)}
          />
        ))
      }
      renderInput={(params) => (
        <StyledTextFieldBootstrap {...params} placeholder="Select store(s)" />
      )}
      value={
        (selectedIds || []).includes(ALL_STORES_VALUE)
          ? [ALL_STORES_OPTION]
          : stores.filter((s) =>
              (selectedIds || []).includes(String(s.store_id ?? s.id)),
            )
      }
      onChange={(_, val) => {
        const ids = [
          ...new Set((val || []).map((row) => String(row.store_id ?? row.id))),
        ];
        // "All" and individual stores are mutually exclusive: picking "All"
        // clears the stores, and adding a store while "All" is on drops "All".
        const hadAll = (selectedIds || []).includes(ALL_STORES_VALUE);
        const others = ids.filter((id) => id !== ALL_STORES_VALUE);
        if (!ids.includes(ALL_STORES_VALUE)) {
          setSelectedIds(others);
        } else if (hadAll && others.length) {
          setSelectedIds(others);
        } else {
          setSelectedIds([ALL_STORES_VALUE]);
        }
      }}
    />
  );

  return (
    <div className={className}>
      <label htmlFor={name}>{label} </label>
      <div className="form-group">
        <FormControl fullWidth>
          {control ? (
            <Controller
              name={name}
              control={control}
              defaultValue={[]}
              render={({ field }) =>
                renderAutocomplete(field.value, field.onChange)
              }
            />
          ) : (
            renderAutocomplete(value, onChange)
          )}
        </FormControl>
      </div>
    </div>
  );
}

export default StoreSelectField;
